import React, { useState, useEffect } from 'react';
import { Row, Col, Table } from 'react-bootstrap';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { ValidationError } from 'yup';
import Text from 'v3/components/Text';
import Select from 'v3/components/Select';
import Input from 'v3/components/Input';
import DatePicker from 'v3/components/DatePicker';
import { useSnackbar } from 'v3/components/Snackbar';
import { useTravel } from 'v3/pages/Travel';
import Modal from 'v3/components/Modal';
import Button from 'v3/components/Button';
import moment from 'moment';
import api, { cooperplaceApi } from 'services/api';
import useErrors from 'hooks/useErrors';
import { formatDateCustom, formatAddress } from 'v3/utils/formatter';

import schema from './schema';

export default function Cte({ isOpen, onClose, emittedId }) {
  const snackbar = useSnackbar();
  const travel = useTravel();

  const [travelNumber, setTravelNumber] = useState(travel.data?.travelNumber);
  const [cteNumber, setCteNumber] = useState('');
  const [cteNumberOld, setCteNumberOld] = useState('');
  const [issuedAt, setIssuedAt] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [destination, setDestination] = useState();
  const [loading, setLoading] = useState(false);

  const [errors, { getErrorsFromYup, clearErrors }] = useErrors({});
  async function fetchEmitted() {
    try {
      setLoading(true);

      const response = await api.get(`loads-emitted/${emittedId}`);

      const emitted = response.data?.[0];
      setCteNumberOld(emitted.cte_number);
      setCteNumber(emitted.cte_number);

      setIssuedAt(
        emitted.date_emitted
          ? formatDateCustom(moment.utc(emitted.date_emitted), 'DD/MM/YYYY HH:mm')
          : '',
      );
      if (emitted?.loadDestination?.destination) {
        const dest = emitted?.loadDestination?.destination;
        setDestination(dest);
      }

      if (emitted.travel_number) {
        setTravelNumber(emitted.travel_number);
      }

      if (emitted.invoicesCte) {
        const invs = emitted.invoicesCte.map((inv) => {
          return {
            value: Number(inv.nf_number),
            label: inv.nf_number,
            key: inv.chave_nf,
            serie: inv?.nf_serie,
          };
        });
        setInvoices(invs);
      }
    } catch (ex) {
      //
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isOpen) {
      fetchEmitted();
    }
  }, [isOpen]);

  async function validateEditCte(data) {
    try {
      const validated = await schema.validate(data, {
        abortEarly: false,
        context: { emitteds: travel.data.emitteds, cteNumberOld },
      });

      if (!validated) {
        snackbar.show(<Text>Dados inválidos.</Text>, {
          type: 'error',
        });
        return false;
      }

      return true;
    } catch (ex) {
    
      if (ex instanceof ValidationError) {
        getErrorsFromYup(ex.inner);
      }
      return false;
    }
  }

  async function submitEditCte() {
    try {
      setLoading(true);

      const valid = await validateEditCte({
        travelNumber,
        cteNumber,
        issuedAt,
        invoices,
        destination,
      });

      if (!valid) {
        snackbar.show(<Text>Dados inválidos.</Text>, {
          type: 'error',
        });
        return;
      }

      const formattedIssuedAt = moment.utc(issuedAt, 'DD/MM/YYYY HH:mm').toISOString();
      const data = {
        cteNumber,
        issuedAt: formattedIssuedAt,
        destinationId: destination.id,
        invoices,
      };

      await cooperplaceApi.put(`loads-emitted/updateCte/${emittedId}`, data);

      snackbar.show(<Text>CT-e {cteNumber} atualizado.</Text>, {
        type: 'success',
      });

      clearErrors();
      onClose();

      await travel.fetchTravel();
    } catch (ex) {
      setLoading(false);

      let message = 'Não foi possível alterar o CT-e.';
      if (ex.response) {
        const { error } = ex.response.data;

        if (error && typeof error === 'string') {
          message = error;
        }
      }

      snackbar.show(<Text>{message}</Text>, {
        type: 'error',
      });
    } finally {
      setLoading(false);
      //
    }
  }

  const closeSubmit = () => {
    clearErrors();
    getErrorsFromYup([]);
    setCteNumber('');
    setCteNumberOld('');
    setIssuedAt('');
    setInvoices([]);
    setDestination('');
    onClose();
  };
  return (
    <Modal
      show={isOpen}
      handleClose={closeSubmit}
      heading={
        <Text type="subheader" color="dark" weight="500">
          Editar Documento {cteNumber}
        </Text>
      }
      body={
        <Row>
          <Col xs={12} className="mb-3">
            <Input
              value={travelNumber}
              disabled={!!travel.data.travelNumber || loading}
              label="Nº da viagem"
              onChange={(event) => setTravelNumber(event.target.value)}
              error={errors.travelNumber}
            />
          </Col>
          <Col xs={6} className="mb-3">
            <Input
              value={cteNumber}
              label="Nº do Documento"
              onChange={(event) => setCteNumber(event.target.value)}
              error={errors.cteNumber}
              disabled={loading}
            />
          </Col>
          <Col className="mb-3">
            <DatePicker
              label="Data de emissão"
              value={issuedAt}
              onChange={(event) => setIssuedAt(event.target.value)}
              withTime
              error={errors.issuedAt}
              disabled={loading}
            />
          </Col>
          <Col xs={12} className="mb-3">
            <Text type="label">Notas fiscais</Text>
            <AsyncCreatableSelect
              value={invoices}
              noOptionsMessage={() => <Text>Digite uma nota fiscal</Text>}
              formatCreateLabel={(value) => <Text>{`Criar "${value}"`} </Text>}
              placeholder={null}
              isMulti
              createOptionPosition="first"
              onChange={(value) => setInvoices(value)}
              disabled={loading}
            />
            {!!errors.invoices && (
              <Text type="little" color="error">
                {errors.invoices}
              </Text>
            )}
          </Col>
          {!!invoices?.length && (
            <Col xs={12}>
              <Table>
                <thead>
                  <tr>
                    <th>
                      <Text type="label">Nº da Nota</Text>
                    </th>
                    <th>
                      <Text type="label">Chave</Text>
                    </th>
                    <th>
                      <Text type="label">Série</Text>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {invoices.map((invoice, index) => (
                    <tr>
                      <td>
                        <Text type="label">{invoice.value}</Text>
                      </td>
                      <td>
                        <Input
                          value={invoice?.key}
                          disabled={loading}
                          onChange={(event) =>
                            setInvoices(
                              invoices.map((item, i) => {
                                if (i === index) {
                                  return { ...item, key: event.target.value };
                                }

                                return item;
                              }),
                            )
                          }
                          error={errors[`invoices[${index}].key`]}
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          value={invoice?.serie}
                          disabled={loading}
                          onChange={(event) =>
                            setInvoices(
                              invoices.map((item, i) => {
                                if (i === index) {
                                  return { ...item, serie: event.target.value };
                                }

                                return item;
                              }),
                            )
                          }
                          error={errors[`invoices[${index}].serie`]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
          )}
          <Col xs={12} className="mb-3">
            <Select
              placeholder={null}
              value={destination}
              label="Destino"
              options={travel.data.loads?.loadDestinations}
              getOptionLabel={(option) => {
                return formatAddress({
                  address: option?.address,
                  number: option?.number,
                  complement: option?.complement,
                  neighborhood: option?.neighborhood,
                  city: option?.city,
                  province: option?.province,
                  zip_code: option?.zipCode || option?.zip_code,
                });
              }}
              getOptionValue={(option) => option.id}
              onChange={(item) => setDestination(item)}
              error={errors.destination}
              isDisabled={loading}
            />
          </Col>
          <Col xs={12} className="text-center mb-3">
            <Button variant="secondary" onClick={submitEditCte} loading={loading} className="py-2">
              <Text type="regular" weight="500">
                Salvar
              </Text>
            </Button>
          </Col>
        </Row>
      }
    />
  );
}
